import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { todayCalendarDate } from '../../lib/calendar-date';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { PermissionNotice, isForbidden } from '../../components/PermissionNotice';
import { isLookupableId } from '../../lib/worker-id';
import { PAY_BASIS_LABELS, workerScreenBasisOptions, workerScreenDefaultBasis, PERCENTAGE_BASE_RATE, type PayBasis } from '../../lib/pay-basis';
import { ArrowLeft, ChevronDown, ChevronRight, Check, AlertCircle } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────
interface PayProfile {
  id?: string;
  user_id: string;
  worker_type: 'employee' | 'contractor' | 'owner_operator';
  primary_vertical: string;
  role_name: string;
  effective_date: string;
  active: boolean;
  /**
   * '' = not chosen yet, on a NEW profile only. The country default fills it
   * (see `effectiveBasis`); a stored basis — any of the five — is kept as is.
   */
  pay_basis: PayBasis | '';
  base_rate: number | '';
  standby_rate: number | '';
  travel_rate: number | '';
  ot_rate_override: number | '';
  h2s_premium_rate: number | '';
  currency: 'CAD' | 'USD';
  province_state: string;
  country: 'CA' | 'US';
  wagepoint_employee_id: string;
  qbo_employee_id: string;
  updated_at?: string;
}

/**
 * A blank profile, its effective date seeded with the TENANT's today
 * (companies.time_zone) — not the device's, and not the day this module
 * happened to load. `?? ''` only seeds the date input: with no zone there is
 * no honest today.
 */
const emptyProfile = (timeZone: string | null): PayProfile => ({
  user_id: '',
  worker_type: 'employee',
  primary_vertical: '',
  role_name: '',
  effective_date: todayCalendarDate(timeZone) ?? '',
  active: true,
  // Resolved in the screen, where `company` is in reach: Day Rate as always,
  // Hourly on a US tenant (lib/pay-basis.ts, workerScreenDefaultBasis).
  pay_basis: '',
  base_rate: '',
  standby_rate: '',
  travel_rate: '',
  ot_rate_override: '',
  h2s_premium_rate: '',
  currency: 'CAD',
  province_state: 'AB',
  country: 'CA',
  wagepoint_employee_id: '',
  qbo_employee_id: '',
});

const VERTICALS = [
  { label: 'Casing Running', value: 'casing_running' },
  { label: 'Trucking', value: 'trucking' },
  { label: 'Well Servicing', value: 'well_servicing' },
  { label: 'Construction', value: 'construction' },
  { label: 'General', value: 'general' },
];
const PROVINCES_STATES = ['AB', 'BC', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'NL', 'PE', 'NT', 'YT', 'NU', 'TX', 'ND', 'CA', 'CO', 'AK', 'NV', 'OK', 'NM', 'WY', 'MT', 'LA', 'OH', 'PA'];

// ─── Segmented control component ────────────────────────────────
function Segmented({ options, value, onChange }: { options: { label: string; value: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex bg-white/5 rounded-lg p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
            value === opt.value
              ? 'bg-amber-500 text-black shadow-sm'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Form field wrappers ────────────────────────────────────────
function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text', prefix, suffix }: {
  value: string | number; onChange: (v: string) => void; placeholder?: string; type?: string; prefix?: string; suffix?: string;
}) {
  return (
    <div className="relative flex items-center">
      {prefix && <span className="absolute left-3 text-gray-500 text-sm pointer-events-none">{prefix}</span>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-16' : ''}`}
      />
      {suffix && <span className="absolute right-3 text-gray-500 text-sm pointer-events-none">{suffix}</span>}
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder }: {
  value: string; onChange: (v: string) => void; options: { label: string; value: string }[]; placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 appearance-none"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ─── Card wrapper ───────────────────────────────────────────────
function Card({ title, children, collapsible, defaultOpen = true }: { title: string; children: React.ReactNode; collapsible?: boolean; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => collapsible && setOpen(!open)}
        className={`w-full flex items-center justify-between px-5 py-4 ${collapsible ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'}`}
      >
        <h3 className="text-white font-semibold text-base">{title}</h3>
        {collapsible && (open ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />)}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">{children}</div>}
    </div>
  );
}

// ─── Toast ──────────────────────────────────────────────────────
/**
 * `tone` exists because this screen has always routed REFUSALS through the same
 * toast as successes — the permission message added with the save mutation's
 * onError rendered on green, under a checkmark. "You don't have permission to
 * change pay profiles" with a tick beside it reads as confirmation of the thing
 * it is refusing, which is the silent-blank-field rule applied to feedback: a
 * wrong signal that looks right is worse than none.
 */
function Toast({ message, tone = 'success', onClose }: { message: string; tone?: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  const isError = tone === 'error';
  return (
    <div
      role="status"
      className={`fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in ${isError ? 'bg-red-600' : 'bg-green-600'}`}
    >
      {isError ? <AlertCircle size={18} /> : <Check size={18} />}
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
}

// ─── Main screen ────────────────────────────────────────────────
export default function WorkerPayProfileScreen() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const { user: authUser, company } = useAuth();
  const timeZone = useTenantTimeZone();
  const isNew = userId === 'new';
  /**
   * A routed id the API cannot look up — which is what a broken caller sends.
   * `/pay-profiles/users/:userId` has NO requireUuid, so the string reaches
   * Postgres, comes back 22P02, and `apiError` maps it to a 400 reading "One of
   * the values sent is not valid for the field it was sent to" — copy that
   * blames the caller for values they never typed. `/users/:id` DOES guard, so
   * it 404s. Two different wrong answers to one malformed link, and neither
   * says the link was malformed.
   *
   * Sending nothing is the honest response: this screen cannot know which
   * worker is meant, and it must not offer a blank CREATE form instead, which
   * is the same trap `profileForbidden` exists to close one branch below.
   */
  const unusableId = !isNew && !isLookupableId(userId);

  const [form, setForm] = useState<PayProfile>(() => ({ ...emptyProfile(timeZone), user_id: userId || '' }));
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const [isExisting, setIsExisting] = useState(false);

  // Fetch existing profile
  const { data: profileData, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['pay-profile', userId],
    queryFn: () => api.payProfiles.getByUser(userId!),
    enabled: !!userId && !isNew && !unusableId,
    // A 403 is a settled answer, not a blip.
    retry: false,
  });

  /**
   * Without this the 403 left `profileData` undefined, `isExisting` false, and the
   * screen rendered its blank CREATE form — telling the caller this worker has no
   * pay profile, and inviting them to type one in and Save into a refusal.
   *
   * Note a 404 is genuinely "no profile yet" here and must NOT be caught by this:
   * `isForbidden` tests the status rather than merely the presence of an error.
   */
  const profileForbidden = isForbidden(profileError);

  // Fetch worker info
  const { data: workerData } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.users.get(userId!),
    enabled: !!userId && !isNew && !unusableId,
  });

  // null when the user row is gone. `workerName` below already falls back to
  // the literal "Worker" for that, which beats a header built from an
  // envelope's undefined first_name/last_name (" " → an empty heading).
  const worker = unwrap<any>(workerData?.data);

  /**
   * Read off the PROFILE payload, not off `form` — the resolved rule is not an
   * editable field and must never round-trip through the save body. Absent
   * stays absent: no live tenant has an overtime rule configured, so `null` here
   * is the ordinary case and renders as an em dash rather than a guess.
   */
  const otRuleLabel: string | null =
    unwrap<any>(profileData?.data)?.overtime_rule?.label ?? null;

  // Populate form when profile loads
  useEffect(() => {
    // The envelope has no `id`, so the old fallthrough left `isExisting` false
    // and the form empty — which is right by accident. Made deliberate: no
    // payload means no profile, so the screen stays in create mode.
    const profile = unwrap<any>(profileData?.data);
    if (profile && profile.id) {
      setIsExisting(true);
      // `overtime_rule` is a read-only projection the API resolves per request,
      // not a column — so it is stripped here rather than spread into the
      // editable form, where `handleSave` would post it straight back. The API
      // ignores unknown keys, so this is not a bug today; it is what stops the
      // next person seeing it in the save body and deciding to persist it, which
      // is the second-answer problem this whole change removed.
      //
      // Note the contrast with `default_pay_code_id`, which IS a column and DOES
      // legitimately round-trip through this spread — see the pay-code tests.
      const { overtime_rule: _resolvedRule, ...editable } = profile;
      setForm({
        ...emptyProfile(timeZone),
        ...editable,
        base_rate: profile.base_rate ?? '',
        standby_rate: profile.standby_rate ?? '',
        travel_rate: profile.travel_rate ?? '',
        ot_rate_override: profile.ot_rate_override ?? '',
        h2s_premium_rate: profile.h2s_premium_rate ?? '',
        wagepoint_employee_id: profile.wagepoint_employee_id ?? '',
        qbo_employee_id: profile.qbo_employee_id ?? '',
      });
    }
  }, [profileData, timeZone]);

  const update = (field: keyof PayProfile, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: any) => {
      const targetUserId = isNew ? data.user_id : userId;
      if (isExisting) {
        return api.payProfiles.updateByUser(targetUserId!, data);
      }
      return api.payProfiles.createByUser(targetUserId!, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-profile'] });
      queryClient.invalidateQueries({ queryKey: ['pay-profiles'] });
      setToast({ message: 'Pay profile saved', tone: 'success' });
      setIsExisting(true);
    },
    // Was onSuccess only — the same shape as the approval mutations in #420. Save
    // 403'd and the screen said nothing, so the click read as a no-op.
    onError: (err: any) => {
      setToast({
        message: isForbidden(err)
          ? "You don't have permission to change pay profiles"
          : err?.response?.data?.error || 'Could not save this pay profile.',
        tone: 'error',
      });
    },
  });

  const handleSave = () => {
    // `employee_pay_profiles.base_rate` is NOT NULL with no default, and this
    // used to map a blank field to `null` — a guaranteed 23502, surfaced as a
    // 500. Refuse here, where the reason can be named, rather than letting the
    // row be rejected downstream.
    //
    // The `isFinite` half is not belt-and-braces: `Number('')` is 0 but
    // `Number('abc')` is NaN, and `JSON.stringify(NaN)` is `null` — so a value
    // the field cannot parse takes the SAME 23502 as a blank one, while looking
    // like a filled-in form. `type="number"` makes that hard to reach in a
    // browser and does not make it impossible.
    // A percentage worker is paid from tickets; base_rate is NOT NULL and the
    // API's contract for it is 0 (lib/pay-basis.ts), so there is nothing to ask.
    const baseRate = isPercentage ? PERCENTAGE_BASE_RATE : Number(form.base_rate);
    if (!isPercentage && (form.base_rate === '' || !Number.isFinite(baseRate))) {
      setToast({ message: 'Base rate is required', tone: 'error' });
      return;
    }

    const { pay_basis: _formBasis, ...rest } = form;
    const payload = {
      ...rest,
      ...(effectiveBasis ? { pay_basis: effectiveBasis } : {}),
      base_rate: baseRate,
      standby_rate: form.standby_rate === '' ? null : Number(form.standby_rate),
      travel_rate: form.travel_rate === '' ? null : Number(form.travel_rate),
      ot_rate_override: form.ot_rate_override === '' ? null : Number(form.ot_rate_override),
      h2s_premium_rate: form.h2s_premium_rate === '' ? null : Number(form.h2s_premium_rate),
    };
    saveMutation.mutate(payload);
  };

  const workerName = worker ? `${worker.first_name || worker.firstName || ''} ${worker.last_name || worker.lastName || ''}`.trim() : (isNew ? 'New Worker' : 'Worker');
  const initials = workerName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  /**
   * The basis this form will save. A stored one wins, always — this screen
   * never re-bases an existing worker. A NEW profile starts where prod always
   * started it, Day Rate — except on a US tenant, where it starts Hourly
   * (api#1097). Canada is deliberately unchanged; see lib/pay-basis.ts.
   */
  const effectiveBasis: PayBasis | '' =
    form.pay_basis || (isExisting ? '' : workerScreenDefaultBasis(company?.country));
  const isPercentage = effectiveBasis === 'percentage';

  const rateLabel = effectiveBasis === 'day_rate' ? '$/day' : effectiveBasis === 'salary' ? '$/year' : '$/hr';

  // Ahead of the loading branch: with both queries disabled there is nothing to
  // wait for, and "Loading..." forever is the failure this replaces.
  if (unusableId) {
    return (
      <div className="min-h-full pb-[var(--bottom-chrome)] bg-background text-white">
        <div className="border-b border-border px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
            <ArrowLeft size={24} /> Back
          </button>
        </div>
        <div className="max-w-[800px] mx-auto px-4 py-20 text-center">
          <p className="text-white font-medium mb-2">This link doesn&rsquo;t name a worker</p>
          <p className="text-sm text-gray-500 mb-6">
            The address is missing the worker it was meant to open, so there is nothing to load.
            Pick the worker from the list instead.
          </p>
          <button
            onClick={() => navigate('/payroll/workers')}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg text-sm transition"
          >
            Go to Workers
          </button>
        </div>
      </div>
    );
  }

  if (profileLoading && !isNew) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center">
        <div className="text-amber-400">Loading...</div>
      </div>
    );
  }

  // Before the form, not inside it: there is nothing on this screen a refused
  // caller may do, so offering the fields at all would be misleading.
  if (profileForbidden) {
    return (
      <div className="min-h-full pb-[var(--bottom-chrome)] bg-background text-white">
        <div className="border-b border-border px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
            <ArrowLeft size={24} /> Back
          </button>
        </div>
        <PermissionNotice detail="Pay rates are visible to admins and office staff. Ask an admin if you need access." />
      </div>
    );
  }

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background text-white">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">{workerName}</h1>
            <p className="text-xs text-gray-500">Pay profile</p>
          </div>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-[800px] mx-auto px-4 py-6 space-y-6">

        {/* Section 1 — Employment details */}
        <Card title="Employment details">
          <Field label="Worker type">
            <Segmented
              options={[
                { label: 'Employee', value: 'employee' },
                { label: 'Contractor', value: 'contractor' },
                { label: 'Owner Operator', value: 'owner_operator' },
              ]}
              value={form.worker_type}
              onChange={(v) => update('worker_type', v)}
            />
          </Field>

          <Field label="Primary vertical">
            <SelectInput
              value={form.primary_vertical}
              onChange={(v) => update('primary_vertical', v)}
              options={VERTICALS}
              placeholder="Select vertical..."
            />
          </Field>

          <Field label="Role name">
            <TextInput
              value={form.role_name}
              onChange={(v) => update('role_name', v)}
              placeholder="e.g. Crew Lead, Driver, Operator"
            />
          </Field>

          <Field label="Effective date">
            <TextInput
              type="date"
              value={form.effective_date}
              onChange={(v) => update('effective_date', v)}
            />
          </Field>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Active</span>
            <button
              type="button"
              onClick={() => update('active', !form.active)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.active ? 'bg-amber-500' : 'bg-white/10'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.active ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </Card>

        {/* Section 2 — Pay basis */}
        <Card title="Pay basis">
          <Field label="Pay basis">
            <Segmented
              // Prod's three for a Canadian tenant; Percentage is offered only
              // on a US one — plus whatever basis a stored profile already has.
              options={workerScreenBasisOptions(company?.country, form.pay_basis).map((value) => ({
                label: PAY_BASIS_LABELS[value],
                value,
              }))}
              value={effectiveBasis}
              onChange={(v) => update('pay_basis', v)}
            />
          </Field>

          {isPercentage ? (
            <p data-testid="pay-profile-percentage-note" className="text-sm text-gray-400">
              Paid from tickets, at the percentages on this worker&rsquo;s pay rates. No base rate is used.
            </p>
          ) : (
          <Field label="Base rate">
            <TextInput
              type="number"
              value={form.base_rate}
              onChange={(v) => update('base_rate', v)}
              prefix="$"
              suffix={rateLabel}
              placeholder="0.00"
            />
          </Field>
          )}

          {effectiveBasis === 'day_rate' && (
            <Field label="Standby rate">
              <TextInput
                type="number"
                value={form.standby_rate}
                onChange={(v) => update('standby_rate', v)}
                prefix="$"
                suffix="$/hr"
                placeholder="0.00"
              />
            </Field>
          )}

          <Field label="Travel rate">
            <TextInput
              type="number"
              value={form.travel_rate}
              onChange={(v) => update('travel_rate', v)}
              prefix="$"
              suffix="$/hr"
              placeholder="0.00"
            />
          </Field>

          <Field label="OT rate override" hint="Leave blank to use calculated OT">
            <TextInput
              type="number"
              value={form.ot_rate_override}
              onChange={(v) => update('ot_rate_override', v)}
              prefix="$"
              suffix="$/hr"
              placeholder="Auto-calculated"
            />
          </Field>

          <Field label="H2S premium rate" hint="Optional">
            <TextInput
              type="number"
              value={form.h2s_premium_rate}
              onChange={(v) => update('h2s_premium_rate', v)}
              prefix="$"
              suffix="$/hr"
              placeholder="0.00"
            />
          </Field>

          <Field label="Currency">
            <Segmented
              options={[
                { label: 'CAD', value: 'CAD' },
                { label: 'USD', value: 'USD' },
              ]}
              value={form.currency}
              onChange={(v) => update('currency', v)}
            />
          </Field>
        </Card>

        {/* Section 3 — Location & compliance */}
        <Card title="Location & compliance">
          <Field label="Country">
            <Segmented
              options={[
                { label: 'Canada', value: 'CA' },
                { label: 'United States', value: 'US' },
              ]}
              value={form.country}
              onChange={(v) => update('country', v)}
            />
          </Field>

          <Field label="Province / State">
            <SelectInput
              value={form.province_state}
              onChange={(v) => update('province_state', v)}
              options={PROVINCES_STATES.map((ps) => ({ label: ps, value: ps }))}
            />
          </Field>

          {/*
            The overtime rule that WOULD APPLY, resolved server-side by the same
            predicate the payroll engine uses (api #412). Read-only on purpose.

            This used to show a string derived in this file from the province —
            "Alberta — 8h/day, 44h/week" — to tenants who had configured no rule
            at all, alongside an "Override" that wrote `ot_rule_override`: a key
            with no column behind it, so it saved, toasted success, and reverted.

            The em dash IS the answer for a tenant with no rule configured, and
            it will read as a regression to anyone who does not know that the
            string it replaces was never sourced from their data.
          */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">OT rule</label>
            <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
              <span className="text-sm text-white">{otRuleLabel ?? '—'}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {otRuleLabel
                ? 'Resolved from this worker’s vertical. Configured in overtime rules.'
                : 'No overtime rule configured for this worker’s vertical.'}
            </p>
          </div>
        </Card>

        {/* Section 4 — Payroll integration */}
        <Card title="Payroll integration" collapsible defaultOpen={false}>
          <Field label="Wagepoint employee ID">
            <TextInput
              value={form.wagepoint_employee_id}
              onChange={(v) => update('wagepoint_employee_id', v)}
              placeholder="Auto-set on export"
            />
          </Field>

          <Field label="QuickBooks employee ID">
            <TextInput
              value={form.qbo_employee_id}
              onChange={(v) => update('qbo_employee_id', v)}
              placeholder="Auto-set on export"
            />
          </Field>

          <p className="text-xs text-gray-500">These are set automatically when you export payroll.</p>
        </Card>
      </div>

      {/* Sticky save button */}
      <div className="border-t border-white/10 mt-6">
        <div className="max-w-[800px] mx-auto px-4 py-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="w-full sm:w-auto px-8 py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl transition disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save pay profile'}
          </button>
        </div>
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
